/*
  Find a Retailer: shared rendering helpers.

  Used by the locator, Find Local, Get Local and the retailer profile. No
  theme dependency. Every string comes from the section's [data-far-i18n]
  element; nothing here is copy.

  window.PwFar.ui.fill(template, values)     replaces [[key]] placeholders
  window.PwFar.ui.el(tag, className, text)
  window.PwFar.ui.i18n(root)                 reads the data-t-* strings of [data-far-i18n] in root
  window.PwFar.ui.safeUrl(url)               http(s) only; bare domains get https:// (doc 04 2.1)
  window.PwFar.ui.distanceText(metres, units, t)
  window.PwFar.ui.card(data, options, t)     a compact retailer card (Find Local, Get Local)
  window.PwFar.ui.retailerData(hit)          card data from a retailer index record
  window.PwFar.ui.dedupe(hits)               drops repeat records of one store (QA FAR-004)
  window.PwFar.ui.prompt(root, config, onLocation)
                                             wires a [data-far-prompt] postal form
*/
(function () {
  'use strict';

  var PwFar = (window.PwFar = window.PwFar || {});
  if (PwFar.ui) return;

  var METRES_PER_MILE = 1609.344;

  function fill(template, values) {
    return String(template || '').replace(/\[\[(\w+)\]\]/g, function (match, key) {
      return values[key] !== undefined ? values[key] : match;
    });
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  // Strings arrive once: Liquid's t output is already HTML-escaped, which is
  // what an attribute needs, and the browser decodes it here. Callers set
  // them with textContent, never innerHTML. data-t-show-more -> show_more.
  function i18n(root) {
    var strings = {};
    var node = root.querySelector('[data-far-i18n]');
    if (!node) return strings;
    Array.prototype.forEach.call(node.attributes, function (attr) {
      if (attr.name.indexOf('data-t-') === 0) strings[attr.name.slice(7).replace(/-/g, '_')] = attr.value;
    });
    return strings;
  }

  function safeUrl(url) {
    if (typeof url !== 'string' || !url.trim()) return null;
    var trimmed = url.trim();
    if (!/^https?:\/\//i.test(trimmed)) trimmed = 'https://' + trimmed.replace(/^\/+/, '');
    try {
      var parsed = new URL(trimmed);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
    } catch (e) {
      return null;
    }
  }

  function distanceText(metres, units, t) {
    if (metres === null || metres === undefined) return '';
    var km = units === 'km';
    var value = (metres / (km ? 1000 : METRES_PER_MILE)).toFixed(1);
    return fill(km ? t.km_value : t.miles_value, { distance: value });
  }

  // Legacy hides the placeholder number some retailers were loaded with.
  function telHref(dial, display) {
    var digits = dial || (display ? display.replace(/[^\d+]/g, '') : '');
    if (!digits || /^\+?1?8888888888$/.test(digits)) return null;
    return 'tel:' + digits;
  }

  function directionsUrl(name, parts) {
    var address = parts.filter(Boolean).join(', ');
    if (!address) return null;
    return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent((name ? name + ', ' : '') + address);
  }

  function newWindowLink(text, href, t, rel) {
    var link = el('a', null, text);
    link.href = href;
    link.target = '_blank';
    link.rel = rel || 'noopener';
    link.appendChild(el('span', 'far-sr-only', ' ' + t.new_window));
    return link;
  }

  /*
    data: {
      name, profileHandle, isDestination, certifiedCurrent, metres,
      street, street2, city, province, postal, country,
      phoneDisplay, phoneDial, phoneExt, website, carries
    }
    carries: true on Find Local, where the store listed this plant. Inventory
    is a retailer-reported list, not a stock check, so the card says
    "carries" and never shows a date (doc 02 D41).
    options: { units, profileRoot, linkProfiles, headingTag }
  */
  function card(data, options, t) {
    var article = el('article', 'far-card');

    var name = el(options.headingTag || 'h3', 'far-card__name');
    if (options.linkProfiles && data.profileHandle && options.profileRoot) {
      var link = el('a', null, data.name);
      link.href = options.profileRoot + encodeURIComponent(data.profileHandle);
      name.appendChild(link);
    } else {
      name.textContent = data.name || '';
    }
    article.appendChild(name);

    var badges = el('ul', 'far-card__badges');
    if (data.isDestination) badges.appendChild(el('li', 'far-badge', t.badge_destination));
    if (data.certifiedCurrent) badges.appendChild(el('li', 'far-badge', t.badge_certified));
    if (badges.children.length) article.appendChild(badges);

    var distance = distanceText(data.metres, options.units, t);
    if (distance) article.appendChild(el('p', 'far-card__distance', distance));

    var address = el('address', 'far-card__address');
    var cityLine = [data.city, [data.province, data.postal].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    [data.street, data.street2, cityLine].filter(Boolean).forEach(function (line, i) {
      if (i) address.appendChild(document.createElement('br'));
      address.appendChild(document.createTextNode(line));
    });
    if (address.childNodes.length) article.appendChild(address);

    if (data.carries && t.carries_plant) article.appendChild(el('p', 'far-card__carries', t.carries_plant));

    var links = el('ul', 'far-card__links');
    var tel = telHref(data.phoneDial, data.phoneDisplay);
    if (tel && data.phoneDisplay) {
      var phoneItem = el('li');
      var phone = el('a', null, data.phoneDisplay);
      phone.href = tel;
      phoneItem.appendChild(phone);
      if (data.phoneExt) phoneItem.appendChild(document.createTextNode(' ' + fill(t.phone_ext, { ext: data.phoneExt })));
      links.appendChild(phoneItem);
    }
    var website = safeUrl(data.website);
    if (website) {
      var siteItem = el('li');
      siteItem.appendChild(newWindowLink(t.website, website, t, 'noopener nofollow'));
      links.appendChild(siteItem);
    }
    var directions = directionsUrl(data.name, [data.street, data.city, data.province, data.postal, data.country]);
    if (directions) {
      var dirItem = el('li');
      dirItem.appendChild(newWindowLink(t.directions, directions, t));
      links.appendChild(dirItem);
    }
    if (links.children.length) article.appendChild(links);

    return article;
  }

  // Card data from a retailer index record (Get Local, and Find Local's
  // nearby stores). Only what a card shows, so a cached copy stays small.
  function retailerData(hit) {
    var a = hit.address || {};
    var phone = (hit.phones || []).filter(function (p) { return p && p.display; })[0] || {};
    return {
      name: hit.name,
      profileHandle: hit.profile_handle,
      isDestination: hit.is_destination === true || hit.list_status === 'destination',
      certifiedCurrent: hit.certified_current === true,
      metres: PwFar.algolia ? PwFar.algolia.distance(hit) : null,
      street: a.street,
      street2: a.street2,
      city: a.city,
      province: a.province,
      postal: a.postal,
      country: hit.country || a.country,
      phoneDisplay: phone.display,
      phoneDial: phone.dial,
      phoneExt: phone.ext,
      website: hit.website
    };
  }

  /*
    The source data can hold one store twice under different retailer_ids
    (QA FAR-004: John Deere Landscape, 21980 Ecorse Rd., Taylor MI). Records
    are the same store when name, street, postal code and country match
    once lowercased, stripped of punctuation, (R) and (TM), with spaces
    collapsed and common street suffixes spelled out. Reads retailer index
    records and inventory index records (retailer_* fields) alike.
  */
  var PUNCTUATION = /[!-\/:-@\[-`{-~¡-¿‐-‧‰-⁞™]/g;
  var STREET_WORDS = { rd: 'road', st: 'street', ave: 'avenue', dr: 'drive', blvd: 'boulevard', hwy: 'highway' };

  function normalise(text) {
    return String(text || '').toLowerCase().replace(PUNCTUATION, '').replace(/\s+/g, ' ').trim();
  }

  function retailerKey(hit) {
    var a = hit.address || {};
    var name = normalise(hit.name || hit.retailer_name);
    var street = normalise(a.street || hit.retailer_street)
      .split(' ')
      .map(function (word) {
        return STREET_WORDS[word] || word;
      })
      .join(' ');
    // A record without a name or street cannot be matched; it is kept.
    if (!name || !street) return null;
    var country = normalise(hit.country || a.country);
    var postal = normalizePostal(a.postal || hit.retailer_postal, country);
    return [name, street, postal, country].join('|');
  }

  // US: the 5-digit ZIP, so ZIP+4 matches it. Canada: upper case without
  // spaces. Anything else: trimmed and upper case.
  function normalizePostal(text, country) {
    var postal = String(text || '').trim().toUpperCase();
    if (country === 'us') return postal.replace(/\D/g, '').slice(0, 5);
    if (country === 'ca') return postal.replace(/\s/g, '');
    return postal;
  }

  // Which of two records of one store is kept: Destination, then Certified,
  // then the rest; then lower list_rank; then nearer; then the
  // alphabetically first profile_handle, so the choice never flickers.
  function statusRank(hit) {
    if (hit.is_destination === true || hit.list_status === 'destination') return 0;
    return hit.list_status === 'certified' ? 1 : 2;
  }

  function outranks(a, b) {
    var sa = statusRank(a);
    var sb = statusRank(b);
    if (sa !== sb) return sa < sb;
    var ra = a.list_rank || 9;
    var rb = b.list_rank || 9;
    if (ra !== rb) return ra < rb;
    var da = PwFar.algolia ? PwFar.algolia.distance(a) : null;
    var db = PwFar.algolia ? PwFar.algolia.distance(b) : null;
    if (da === null) da = Infinity;
    if (db === null) db = Infinity;
    if (da !== db) return da < db;
    return String(a.profile_handle || '') < String(b.profile_handle || '');
  }

  // The hits with every repeat store removed, in their original order.
  function dedupe(hits) {
    var keys = hits.map(retailerKey);
    var best = {};
    hits.forEach(function (hit, i) {
      var key = keys[i];
      if (key !== null && (!best[key] || outranks(hit, best[key]))) best[key] = hit;
    });
    return hits.filter(function (hit, i) {
      return keys[i] === null || best[keys[i]] === hit;
    });
  }

  /*
    Wires the shared "where are you" form inside root:
      [data-far-prompt]        the form (postal input named "postal")
      [data-far-prompt-error]  where a bad or unknown code is reported
    onLocation(loc) runs after a code resolves; the location is then saved,
    which also tells every other Find a Retailer feature on the page.
  */
  function prompt(root, config, t, onLocation) {
    var form = root.querySelector('[data-far-prompt]');
    if (!form) return;
    var input = form.elements.postal;
    var error = root.querySelector('[data-far-prompt-error]');
    function showError(text) {
      if (!error) return;
      error.textContent = text || '';
      error.hidden = !text;
    }
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var text = input.value;
      if (!PwFar.location.parse(text)) {
        input.setAttribute('aria-invalid', 'true');
        showError(t.invalid);
        input.focus();
        return;
      }
      input.removeAttribute('aria-invalid');
      showError('');
      PwFar.location.resolve(config, text).then(
        function (loc) {
          if (!loc) {
            input.setAttribute('aria-invalid', 'true');
            showError(fill(t.not_found, { postal: text.trim().toUpperCase() }));
            return;
          }
          // The owning widget takes the location first, so the change event
          // save() fires is recognised as its own; the others follow it.
          onLocation(loc);
          PwFar.location.save(loc);
        },
        function () {
          showError(t.load_error);
        }
      );
    });
  }

  PwFar.ui = {
    fill: fill,
    el: el,
    i18n: i18n,
    safeUrl: safeUrl,
    distanceText: distanceText,
    telHref: telHref,
    directionsUrl: directionsUrl,
    card: card,
    retailerData: retailerData,
    dedupe: dedupe,
    prompt: prompt
  };
})();
